import { z } from "zod";
import { parsePhoneNumberFromString } from "libphonenumber-js";

/**
 * UI-oriented schema helpers.
 *
 * Goal:
 * - Text inputs in the UI are strings
 * - Empty / undefined / null should behave like "empty input"
 * - Required fields should show human messages, never type-system errors
 */

const asTrimmedString = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  return String(value).trim();
};

const requiredText = (message: string, max?: number) => {
  let schema = z.string().trim().min(1, message);
  if (max != null) schema = schema.max(max);
  return z.preprocess((v) => asTrimmedString(v), schema);
};

const optionalText = (max: number) =>
  z.preprocess(
    (v) => asTrimmedString(v),
    z.string().trim().max(max),
  );

const requiredUuid = (message: string) =>
  z.preprocess(
    (v) => asTrimmedString(v),
    z.uuid(message),
  );

const optionalUuid = (message = "Ungültige Auswahl") =>
  z.preprocess(
    (v) => asTrimmedString(v),
    z.string().refine((value) => value === "" || z.uuid().safeParse(value).success, {
      message,
    }),
  );

const optionalEmail = (message = "Ungültige E-Mail-Adresse") =>
  z.preprocess(
    (v) => asTrimmedString(v),
    z.string().refine(
      (value) => value === "" || z.email().safeParse(value).success,
      { message },
    ),
  );

const optionalDate = () =>
  z.preprocess((value) => {
    if (value == null || value === "") return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    return value;
  }, z.date().nullable());

export const optionalGermanPhone = (
  message = "Ungültige Telefonnummer",
) =>
  z.preprocess(
    (value) => asTrimmedString(value),
    z.string().refine((value) => {
      if (value === "") return true;

      const phone = parsePhoneNumberFromString(value, {
        defaultCountry: "DE",
        extract: false,
      });

      return phone?.isValid() ?? false;
    }, { message }),
  );

const addressSchema = z.object({
  label: optionalText(120),
  street: requiredText("Straße ist erforderlich", 120),
  streetNumber: requiredText("Hausnummer ist erforderlich", 40),
  zip: requiredText("PLZ ist erforderlich", 20),
  city: requiredText("Ort ist erforderlich", 120),
  country: requiredText("Land ist erforderlich", 2),
});

const existingClientSchema = z.object({
  mode: z.literal("existing"),
  clientId: requiredUuid("Kunde ist erforderlich"),
});

const newClientSchema = z.object({
  mode: z.literal("new"),
  name: requiredText("Name ist erforderlich", 120),
  contactName: optionalText(120),
  phone: optionalGermanPhone(),
  email: optionalEmail(),
  address: addressSchema,
});

const existingConstructionSiteSchema = z.object({
  mode: z.literal("existing"),
  constructionSiteId: requiredUuid("Einsatzort ist erforderlich"),
});

const clientAddressConstructionSiteSchema = z.object({
  mode: z.literal("client_address"),
  label: optionalText(120),
});

const newAddressConstructionSiteSchema = z.object({
  mode: z.literal("new_address"),
  label: optionalText(120),
  address: addressSchema,
});

export const jobIntakeFormSchema = z
  .object({
    job: z.object({
      title: requiredText("Titel ist erforderlich", 200),
      description: optionalText(4000),
      notes: optionalText(4000),
      plannedStartAt: optionalDate(),
      assignedPersonId: optionalUuid(),
    }),
    client: z.discriminatedUnion("mode", [
      existingClientSchema,
      newClientSchema,
    ]),
    constructionSite: z.discriminatedUnion("mode", [
      existingConstructionSiteSchema,
      clientAddressConstructionSiteSchema,
      newAddressConstructionSiteSchema,
    ]),
  })
  .superRefine((value, ctx) => {
    if (
      value.client.mode === "new" &&
      value.constructionSite.mode === "existing"
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["constructionSite", "mode"],
        message:
          "Bei neuem Kunden kann kein bestehender Einsatzort gewählt werden.",
      });
    }
  });

const existingAddressSchema = z.object({
  mode: z.literal("existing_address"),
  addressId: requiredUuid("Adresse ist erforderlich"),
});

const clientAddressSchema = z.object({
  mode: z.literal("client_address"),
});

const newAddressSchema = z.object({
  mode: z.literal("new_address"),
  address: addressSchema,
});

export const constructionSiteIntakeFormSchema = z
  .object({
    client: z.discriminatedUnion("mode", [
      existingClientSchema,
      newClientSchema,
    ]),
    constructionSite: z.object({
      label: optionalText(120),
      address: z.discriminatedUnion("mode", [
        existingAddressSchema,
        clientAddressSchema,
        newAddressSchema,
      ]),
    }),
  })
  .superRefine((value, ctx) => {
    if (
      value.client.mode === "new" &&
      value.constructionSite.address.mode === "existing_address"
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["constructionSite", "address", "mode"],
        message:
          "Bei neuem Kunden kann keine bestehende Adresse gewählt werden.",
      });
    }
  });

const clientBaseSchema = {
  name: requiredText("Name ist erforderlich", 120),
  contactName: optionalText(120),
  phone: optionalGermanPhone(),
  email: optionalEmail(),
};

const existingAddressClientSchema = z.object({
  ...clientBaseSchema,
  addressMode: z.literal("existing"),
  addressId: requiredUuid("Adresse ist erforderlich"),
});

const newAddressClientSchema = z.object({
  ...clientBaseSchema,
  addressMode: z.literal("new"),
  address: addressSchema,
});

export const clientCreateFormSchema = z.discriminatedUnion("addressMode", [
  existingAddressClientSchema,
  newAddressClientSchema,
]);

export const personCreateFormSchema = z.object({
  displayName: requiredText("Name ist erforderlich", 120),
  preferredName: optionalText(120),
  mobilePhoneE164: optionalGermanPhone(),
  email: optionalEmail(),
  canAppearInPhoto: z.coerce.boolean().default(false),
});

export const addressEditFormSchema = z.object({
  label: optionalText(120),
  street: requiredText("Straße ist erforderlich", 120),
  streetNumber: requiredText("Hausnummer ist erforderlich", 40),
  zip: requiredText("PLZ ist erforderlich", 20),
  city: requiredText("Ort ist erforderlich", 120),
  country: optionalText(2),
});

export const clientEditFormSchema = z.object({
  name: requiredText("Name ist erforderlich", 120),
  contactName: optionalText(120),
  phone: optionalGermanPhone(),
  email: optionalEmail(),
  addressId: optionalUuid("Ungültige Adresse"),
});

export const constructionSiteEditFormSchema = z.object({
  label: optionalText(120),
  clientId: optionalUuid("Ungültiger Kunde"),
  addressId: optionalUuid("Ungültige Adresse"),
});

export const jobEditFormSchema = z.object({
  title: requiredText("Titel ist erforderlich", 200),
  constructionSiteId: optionalUuid("Ungültiger Einsatzort"),
  description: optionalText(4000),
  notes: optionalText(4000),
  plannedStartAt: optionalDate(),
  assignedPersonId: optionalUuid("Ungültige Person"),
});

export const personEditFormSchema = z.object({
  displayName: optionalText(120),
  preferredName: optionalText(120),
  mobilePhoneE164: optionalGermanPhone(),
  email: optionalEmail(),
  canAppearInPhoto: z.boolean().nullable().optional(),
});

export type JobIntakeFormInput = z.input<typeof jobIntakeFormSchema>;
export type JobIntakeFormValues = z.output<typeof jobIntakeFormSchema>;
export type ConstructionSiteIntakeFormInput = z.input<typeof constructionSiteIntakeFormSchema>;
export type ConstructionSiteIntakeFormValues = z.output<typeof constructionSiteIntakeFormSchema>;
export type ClientCreateFormInput = z.input<typeof clientCreateFormSchema>;
export type ClientCreateFormValues = z.output<typeof clientCreateFormSchema>;
export type PersonCreateFormInput = z.input<typeof personCreateFormSchema>;
export type PersonCreateFormValues = z.output<typeof personCreateFormSchema>;
export type AddressEditFormInput = z.input<typeof addressEditFormSchema>;
export type AddressEditFormValues = z.output<typeof addressEditFormSchema>;
export type ClientEditFormInput = z.input<typeof clientEditFormSchema>;
export type ClientEditFormValues = z.output<typeof clientEditFormSchema>;
export type ConstructionSiteEditFormInput = z.input<typeof constructionSiteEditFormSchema>;
export type ConstructionSiteEditFormValues = z.output<typeof constructionSiteEditFormSchema>;
export type JobEditFormInput = z.input<typeof jobEditFormSchema>;
export type JobEditFormValues = z.output<typeof jobEditFormSchema>;
export type PersonEditFormInput = z.input<typeof personEditFormSchema>;
export type PersonEditFormValues = z.output<typeof personEditFormSchema>;
